import { type ReactNode, useState } from "react";
import "./ScheduleImportDayDialog.scss";
import CustomDialog from "@/shared/components/generic/custom-dialog/CustomDialog.tsx";
import { useTranslation } from "react-i18next";
import DialogActionsButtons from "@/shared/components/generic/dialog-actions-buttons/DialogActionsButtons.tsx";
import Button from "@/shared/components/generic/button/Button.tsx";
import { Level, Size } from "@/shared/components/generic/UiEnums.ts";
import DropDownDatePicker from "@/pages/human-resources/time-management/schedules/site-schedule/day-view/import-dialog/DropDownDatePicker.tsx";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";
import useSchedulesManager from "@/pages/human-resources/time-management/schedules/site-schedule/model/schedule-data/useSchedulesManager.ts";
import { DateTime } from "luxon";
import ImportActionSelector from "@/pages/human-resources/time-management/components/schedule-import/ImportActionSelector.tsx";
import type { ImportAction } from "@/pages/human-resources/time-management/components/schedule-import/model/ImportAction.ts";
import useImportDaySchedule from "@/pages/human-resources/time-management/schedules/site-schedule/day-view/import-dialog/useImportDaySchedule.ts";
import IconCalendarIncomingArrow from "@/shared/components/generic/icons/IconCalendarIncomingArrow.tsx";

interface Properties {
  onClose: () => void;
}

export default function ScheduleImportDayDialog(props: Properties): ReactNode {
  const importDaySchedule = useImportDaySchedule();
  const { t: tCommon } = useTranslation("common");
  const { t: tDialog } = useTranslation("humanResources", {
    keyPrefix: "timeManagement.scheduleImportDialog",
  });
  const {
    siteData: {
      siteAddress: { timeZone },
    },
  } = useScheduleReferenceData();
  const {
    contractsManager: { startDate: scheduleDate },
  } = useSchedulesManager();
  const [applyButtonDisabled, setApplyButtonDisabled] = useState(false);

  const [selectedDate, setSelectedDate] = useState(
    DateTime.fromJSDate(scheduleDate, { zone: timeZone })
      .minus({ day: 1 })
      .toJSDate(),
  );

  const [selectedAction, setSelectedAction] =
    useState<ImportAction>("fillEmpty");

  return (
    <CustomDialog
      open={true}
      onClose={props.onClose}
      title={tDialog("day.title")}
      icon={IconCalendarIncomingArrow}
    >
      <div className="schedule-import-day-dialog">
        <div className="date-prompt">
          <div>{tDialog("day.selectDay")}</div>
          <DropDownDatePicker
            timeZone={timeZone}
            value={selectedDate}
            onChange={setSelectedDate}
            shouldDisableDate={(d) => d.toMillis() === scheduleDate.getTime()}
          />
        </div>
        <ImportActionSelector
          action={selectedAction}
          onChange={setSelectedAction}
        />
        <DialogActionsButtons>
          <Button
            onClick={props.onClose}
            label={tCommon("actions.cancel")}
            size={Size.SMALL}
            level={Level.SECONDARY}
          />
          <Button
            disabled={applyButtonDisabled}
            onClick={() => {
              setApplyButtonDisabled(true);
              void importDaySchedule(selectedAction, selectedDate).finally(
                () => {
                  setApplyButtonDisabled(false);
                  props.onClose();
                },
              );
            }}
            label={tCommon("actions.apply")}
            size={Size.SMALL}
            level={Level.PRIMARY}
          />
        </DialogActionsButtons>
      </div>
    </CustomDialog>
  );
}
